import React, { createContext, useContext, useEffect, useState } from "react";
import { useInitialValues } from "./use-initial-values";
import { mapFiltersToTravelRequestQueryInput } from "../utils/mapping";
import { isEqual, omit } from "lodash";
import { UseSearchFilters, useSearchFilters } from "@bonliva-crm/ui/search-filters";
import { TravelRequestQueryInput } from "@graphql";
import { TravelRequestFilterState } from "../utils/travel-request-filter-state";

export type TravelRequestFilter = TravelRequestQueryInput;

type TravelRequestSearchFiltersReturnType = {
  queryInput: TravelRequestFilter;
  filterManager: UseSearchFilters<TravelRequestFilterState>;
  /**
   * Form has non-empty value in any field
   */
  hasValues: boolean;
};

const TravelRequestSearchFiltersContext =
  createContext<TravelRequestSearchFiltersReturnType>(null!);

export const useTravelRequestSearchFilters = () => {
  const context = useContext(TravelRequestSearchFiltersContext);

  if (context == null) {
    throw new Error(
      "useTravelRequestSearchFilters must be used within TravelRequestSearchFiltersProvider"
    );
  }

  return context;
};

export const TravelRequestSearchFiltersProvider = ({
  children
}: {
  children: any;
}) => {
  const { initialValues, emptyValues } = useInitialValues();

  const [queryInput, setQueryInput] = useState<TravelRequestFilter>(
    mapFiltersToTravelRequestQueryInput(initialValues)
  );

  const [hasValues, setHasValues] = useState(false);

  const filterManager = useSearchFilters({
    initialValues: initialValues,
    localStorageKey: "TravelRequestSearchFavoriteFields",
    activeFilters: [
      "senderIds",
      "responsiblePerson",
      "travelRequestStatusFilter",
      "createdFromTo"
    ],
    // parseSearchProfileFilters: (f) => f,
    onChange: (value) => {
      setQueryInput(mapFiltersToTravelRequestQueryInput(value));
    }
  });

  const values = filterManager.formMethods.watch();

  useEffect(() => {
    const omitFields = ["sort", "text"];
    setHasValues(!isEqual(omit(emptyValues, omitFields), omit(values, omitFields)));
  }, [emptyValues, values]);

  return (
    <TravelRequestSearchFiltersContext.Provider
      value={{
        queryInput,
        filterManager,
        hasValues
      }}
    >
      {children}
    </TravelRequestSearchFiltersContext.Provider>
  );
};
